import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PAGES_AND_PATHS } from 'src/app/constants/pages-and-paths';
import { ShareEntitiesEnums } from 'src/app/enums/chat-enums';
import { TlPagination } from 'src/app/interfaces/general-interfaces';
import { AuthenticationHelperService } from 'src/app/services/helpers/authentication-helper.service';
import { ListsHelperService } from 'src/app/services/helpers/lists-helper.service';
import { ItemsHttpService } from 'src/app/services/http-services/items/items-http.service';
import { DeleteModalText } from 'src/app/services/http-services/lists/lists-http-intterfaces';
import {UserRoles} from '../../../enums/general-enums';

@Component({
  selector: 'app-single-review',
  templateUrl: './single-review.component.html',
})
export class SingleReviewComponent implements OnInit {
  type = '';
  reviewId = 0;
  typeId = 0;
  review: any;
  comments: any[] = [];
  pagination!: TlPagination;
  isDropdownOpen = false;
  isDeleteModalOpen = false;
  modalText: DeleteModalText = { title: '', description: '', button: '' };
  loggedUser: any;
  isSharePostModalOpen = false;
  ShareEntitiesEnums = ShareEntitiesEnums;
  reviewUrl = '';
  commentForm!: FormGroup;
  PAGES_AND_PATHS = PAGES_AND_PATHS;
  commentForEdit: any = null;
  commentForDelete: any = null;
  isReportModalOpen = false;

  constructor(
    private actRoute: ActivatedRoute,
    private itemsHttpService: ItemsHttpService,
    private router: Router,
    private toastService: ToastrService,
    private formBuilder: FormBuilder,
    private authHelper: AuthenticationHelperService,
    public listsHelperService: ListsHelperService,
    private meta: Meta,
  ) {
  }

  ngOnInit(): void {
    this.loggedUser = this.authHelper.getUserData;
    this.buildForm();
    this.getParams();
  }

  buildForm(): void {
    this.commentForm = this.formBuilder.group({
      comment: ['', [Validators.required]],
    });
  }

  getParams(): void {
    this.actRoute.paramMap.subscribe(params => {
      this.reviewId = Number(params.get('id'));
      this.typeId = Number(params.get('typeId'));
      this.type = params.get('type') as string;
      if (this.type === 'list') {
        this.getSingleListReview(this.reviewId, this.typeId);
        this.getPublicSingleListReview(this.reviewId);
      } else {
        this.getSingleItemReview(this.reviewId, this.typeId);
        this.getPublicSingleItemReview(this.reviewId);
      }
    });
  }

  getSingleListReview(reviewId: number, typeId: number): void {
    this.itemsHttpService.getSingleListReview(reviewId, typeId).then((response: any) => {
      this.review = response.data.review;
      this.comments = response.data.comments.data;
      this.pagination = response.data.comments;
    });
  }

  getPublicSingleListReview(reviewId: number): void {
    this.itemsHttpService.getPublicReview(reviewId).then((response: any) => {
      const publicReview = response.data.review;
      this.meta.updateTag({
        property: 'og:title',
        content: `TheLysts review of ${publicReview.reviewable?.title} by ${publicReview.author?.username}`
      });
      this.meta.updateTag({
        property: 'og:description',
        content: `Rating: ${publicReview.rating} “${publicReview.review}”`
      });
      this.meta.updateTag({
        property: 'og:image',
        content: publicReview.reviewable.lyst_image_url !== null ? publicReview.reviewable.lyst_image_url : 'assets/images/og-image.png'
      });
      this.meta.updateTag({
        property: 'og:url', content: this.router.url
      });
    });
  }

  getSingleItemReview(reviewId: number, typeId: number): void {
    this.itemsHttpService.getSingleItemReview(reviewId, typeId).then((response: any) => {
      this.review = response.data.review;
      this.comments = response.data.comments.data;
      this.pagination = response.data.comments;
    });
  }

  getPublicSingleItemReview(reviewId: number): void {
    this.itemsHttpService.getPublicReview(reviewId).then((response: any) => {
      const publicReview = response.data.review;
      this.meta.updateTag({
        property: 'og:title',
        content: `TheLysts review of ${publicReview.reviewable.name} by ${publicReview.author.username}`
      });
      this.meta.updateTag({
        property: 'og:description',
        content: `Rating: ${publicReview.rating} “${publicReview.review}”`
      });
      this.meta.updateTag({
        property: 'og:image',
        content: publicReview.reviewable.lyst_item_image_url !== null ? publicReview.reviewable.lyst_item_image_url : 'assets/images/og-image.png'
      });
      this.meta.updateTag({
        property: 'og:url', content: this.router.url
      });
    });
  }

  openViewMore(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  openEditReview() {
    const reviewDetails = {
      obj: this.review
    }
    this.router.navigate([PAGES_AND_PATHS.updateReview.pagePath, this.type === 'list' ? 'list' : 'item'], { state: reviewDetails })
  }

  openDeleteModal() {
    this.isDeleteModalOpen = true;
    this.modalText.title = 'Delete review?';
    this.modalText.description = 'Are you sure you wish to delete this review? This action cannot be undone.';
    this.modalText.button = 'GENERAL.delete';
  }

  takeAction(): void {
    this.type === 'list' ? this.deleteListReview() : this.deleteItemReview();
  }

  deleteItemReview(): void {
    this.isDeleteModalOpen = false;
    this.itemsHttpService.deleteItemReview(this.review.id).then((response: any) => {
      this.toastService.success(response.message);
      this.router.navigate([PAGES_AND_PATHS.singleItem.pagePath, this.typeId]);
    })
  }

  deleteListReview(): void {
    this.isDeleteModalOpen = false;
    this.itemsHttpService.deleteListReview(this.review.id).then((response: any) => {
      this.toastService.success(response.message);
      this.router.navigate([PAGES_AND_PATHS.singleList.pagePath, this.typeId, 'reviews']);
    })
  }

  shareReview(): void {
    this.isSharePostModalOpen = true;
  }

  toggleLikeReview(review: any): void {
    this.itemsHttpService.addReviewToFavorites(review.id).then((response: any) => {
      this.review = response.data;
    })
  }

  toggleLikeComment(comment: any): void {
    this.itemsHttpService.addCommentToFavorites(comment.id).then((response: any) => {
      const index = this.comments.findIndex(obj => obj.id === comment.id);
      this.comments[index].am_i_liked_this = response.data.am_i_liked_this;
      this.comments[index].likes_count = response.data.likes_count;
    })
  }

  saveComment(): void {
    if (this.commentForm.valid) {
      this.commentForEdit ? this.updateComment() : this.storeComment();
    }
  }

  storeComment(): void {
    this.itemsHttpService.storeReviewComment(this.review.id, this.commentForm.value).then((response: any) => {
      this.toastService.success(response.message);
      this.commentForm.reset();
      // this.comments.push(response.data);
      this.type === 'list' ? this.getSingleListReview(this.reviewId, this.typeId) : this.getSingleItemReview(this.reviewId, this.typeId);
    })
  }

  updateComment(): void {
    this.itemsHttpService.updateReviewComment(this.commentForEdit.id, this.commentForm.value).then((response: any) => {
      this.toastService.success(response.message);
      this.commentForm.reset();
      // this.comments.push(response.data);
      this.type === 'list' ? this.getSingleListReview(this.reviewId, this.typeId) : this.getSingleItemReview(this.reviewId, this.typeId);
    })
  }

  edit(comment: any): void {
    this.commentForEdit = comment;
    this.commentForm.patchValue(comment);
  }

  deleteCommnet(): void {
    this.itemsHttpService.deleteReviewComment(this.commentForDelete.id).subscribe((response: any) => {
      this.comments = this.comments.filter(comment => comment.id !== this.commentForDelete.id);
      this.toastService.success(response.message);
      this.commentForDelete = null;
      this.type === 'list' ? this.getSingleListReview(this.reviewId, this.typeId) : this.getSingleItemReview(this.reviewId, this.typeId);
    })
  }

  protected readonly UserRoles = UserRoles;
}
